import React, { useId, useState } from 'react';
import Button from '../ui/Button';
import SearchableSelect from '../ui/SearchableSelect';
import GiveLoginPanel from './GiveLoginPanel';
import { formatDateTime } from '../../utils/formatDate';
import { ROLE_BADGE_CLASS, ROLE_DESCRIPTIONS, ROLE_LABELS, ROLE_ORDER } from '../../utils/roles';
import type { UserRole } from '../../types/User';
import type { Person } from '../../types/People';

interface PersonLoginSectionProps {
  person: Person;
  /** Only an owner can give a login, change its role or remove it (the API refuses everyone else). */
  isOwner: boolean;
  /** The login is the signed-in person's own: its role and removal are not offered here. */
  isMe: boolean;
  /** The person is archived: nothing here can be changed. */
  readOnly: boolean;
  /** Unsaved details: a login is created from the saved e-mail, so ask for Save first. */
  detailsDirty: boolean;
  roleDraft: UserRole;
  roleError: string | null;
  onRoleChange: (role: UserRole) => void;
  onGiveLogin: (role: UserRole, email?: string) => Promise<void>;
  onRemoveLogin: () => void;
}

/**
 * The person's sign-in: whether they have one, its access role (one value for the default for the workspace),
 * the last sign-in, and giving or removing the login.
 */
const PersonLoginSection: React.FC<PersonLoginSectionProps> = ({
  person, isOwner, isMe, readOnly, detailsDirty, roleDraft, roleError, onRoleChange, onGiveLogin, onRemoveLogin,
}) => {
  const uid = useId();
  const [giving, setGiving] = useState(false);
  const { login } = person;

  return (
    <section className="settings-card ep-section" aria-labelledby={`${uid}-title`}>
      <h2 id={`${uid}-title`} className="ap-section-title">Login</h2>
      <p className="settings-muted-xs">
        A login&apos;s access role is its default for every project in the workspace. A different role can be set for a
        single project under Access on projects; the roles further down are the person&apos;s job on each project.
      </p>

      {!login ? (
        <>
          <p className="ep-status"><span className="ds-badge ds-badge-neutral">No login</span> This person is a contact and cannot sign in.</p>
          {giving ? (
            <GiveLoginPanel
              person={person}
              onCancel={() => setGiving(false)}
              onCreate={async (role, email) => { await onGiveLogin(role, email); setGiving(false); }}
            />
          ) : isOwner && !readOnly ? (
            <div className="ep-actions">
              <Button type="button" variant="secondary" size="sm" disabled={detailsDirty} onClick={() => setGiving(true)}>Give login</Button>
              {detailsDirty && <span className="settings-muted-xs">Save your changes first; a login uses the saved e-mail.</span>}
            </div>
          ) : (
            <p className="settings-muted-xs">Only an owner can give a login.</p>
          )}
        </>
      ) : (
        <>
          <p className="ep-status">
            <span className={`ds-badge ${ROLE_BADGE_CLASS[login.role] ?? 'ds-badge-neutral'}`}>{ROLE_LABELS[login.role] ?? login.role}</span>
            <span className="um-last-login">
              {login.last_login_at ? `Last login ${formatDateTime(login.last_login_at)}` : 'Never logged in'}
            </span>
            {login.email && <span className="ep-status__mail">{login.email}</span>}
          </p>
          {isOwner && !isMe && !readOnly ? (
            <div className="form-group ep-role">
              <span className="form-label" id={`${uid}-role`}>Access role (whole workspace)</span>
              <SearchableSelect
                value={roleDraft}
                onChange={value => onRoleChange(value as UserRole)}
                options={ROLE_ORDER.map(r => ({ value: r, label: ROLE_LABELS[r] }))}
                ariaLabel="Access role for the whole workspace"
              />
              <p className="form-hint">{ROLE_DESCRIPTIONS[roleDraft]}. Applies to every project unless a project sets its own.</p>
              {roleError && <p className="form-error-text" role="alert">{roleError}</p>}
            </div>
          ) : (
            <p className="settings-muted-xs">
              {isMe ? 'This is your own login, so its role is not changed here.' : 'Only an owner can change a login’s role.'}
            </p>
          )}
          {isOwner && !isMe && !readOnly && (
            <div className="ep-actions">
              <Button type="button" variant="ghost" size="sm" onClick={onRemoveLogin}>Remove login</Button>
              <span className="settings-muted-xs">They can no longer sign in. They stay in the directory.</span>
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default PersonLoginSection;
